import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const STORAGE_PREFIX = "confirmph_demo_ack_v1:";

/**
 * DemoBusinessGuard: renders a sticky orange banner + first-visit modal
 * across every public demo-business page (legacy profile, mini-site, in-app
 * booking flow). Only renders when `isDemo` is truthy: no overhead for
 * real businesses.
 *
 * The first-visit modal is dismissed per browser (localStorage key scoped
 * by company id), but the sticky banner is permanent: visitors can never
 * forget they are on a demo page.
 *
 * Props:
 *  - isDemo: boolean: set by the calling page based on company.is_demo
 *  - companyId: string: used to scope the "I understand" acknowledgement
 */
export default function DemoBusinessGuard({ isDemo, companyId }) {
  const [open, setOpen] = useState(false);
  const { panelRef } = useAccessibleOverlay(open, acknowledge);

  useEffect(() => {
    if (!isDemo) return undefined;
    const key = STORAGE_PREFIX + (companyId || "*");
    try {
      const seen = localStorage.getItem(key);
      if (!seen) setOpen(true);
    } catch {
      setOpen(true);
    }
    return undefined;
  }, [isDemo, companyId]);

  if (!isDemo) return null;

  function acknowledge() {
    try {
      localStorage.setItem(STORAGE_PREFIX + (companyId || "*"), new Date().toISOString());
    } catch { /* ignore */ }
    setOpen(false);
  }

  return (
    <>
      {/* Sticky banner: fixed top, always visible */}
      <div
        data-testid="demo-business-banner"
        role="alert"
        className="fixed top-0 inset-x-0 z-[60] bg-red-600 text-white shadow-md"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center gap-2 text-xs sm:text-sm font-semibold">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden />
          <span className="leading-tight">
            <span className="uppercase tracking-wide">Demo business: </span>
            Nothing on this page is real. Do not book, pay, or rely on this information.
          </span>
        </div>
      </div>
      {/* Spacer so the sticky banner doesn't overlap the page header */}
      <div aria-hidden className="h-9 sm:h-10" />

      {open && (
        <div
          ref={panelRef}
          tabIndex={-1}
          data-testid="demo-business-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-modal-title"
          className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">
            <div className="bg-red-600 text-white px-5 py-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" aria-hidden />
              <h2 id="demo-modal-title" className="font-display text-lg sm:text-xl font-semibold">
                This is a demo business
              </h2>
              <button
                type="button"
                onClick={acknowledge}
                aria-label="Close"
                data-testid="demo-business-modal-close"
                className="ml-auto text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-5 text-sm text-slate-700 leading-relaxed">
              <p>This page is for testing and demonstration only.</p>
              <p className="mt-3">
                Nothing shown here is a real rental business, real vehicle, real price, real
                booking, real availability, or real offer.
              </p>
              <p className="mt-3 font-semibold text-slate-900">
                Do not contact, pay, reserve, or rely on anything shown on this demo page.
              </p>
            </div>
            <div className="px-5 pb-5">
              <button
                type="button"
                onClick={acknowledge}
                data-testid="demo-business-modal-ack"
                className="w-full px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                I understand
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
